/**
 * Browser-based RFC-4180 CSV Serializer and File Downloader
 */

/**
 * Escapes a cell value according to RFC-4180 rules
 */
export function escapeCsvCell(value) {
  if (value === null || value === undefined) {
    return "";
  }
  const str = String(value);
  // If string contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Converts tabular rows into an RFC-4180 compliant CSV string
 * @param {Array<{ header: string, key?: string, format?: Function }>} columns
 * @param {Array<Object>} rows
 * @returns {string} CSV string with UTF-8 BOM
 */
export function generateCsvString(columns, rows) {
  if (!columns || !columns.length) return "";

  const headerLine = columns.map((col) => escapeCsvCell(col.header || col.key || "")).join(",");

  const rowLines = (rows || []).map((row) => {
    return columns
      .map((col) => {
        let val;
        if (typeof col.format === "function") {
          val = col.format(row);
        } else if (col.key) {
          val = row[col.key];
        } else {
          val = "";
        }
        return escapeCsvCell(val);
      })
      .join(",");
  });

  // Prepend UTF-8 BOM (\uFEFF) so Excel properly detects UTF-8 characters (e.g. Rupee symbol, accented names)
  return "\uFEFF" + [headerLine, ...rowLines].join("\r\n");
}

/**
 * Triggers a browser download of CSV data
 * @param {string} filename - Desired download filename (e.g. "cashfree_audit_2026-10-26.csv")
 * @param {Array<{ header: string, key?: string, format?: Function }>} columns
 * @param {Array<Object>} rows
 */
export function downloadCsv(filename, columns, rows) {
  const csvContent = generateCsvString(columns, rows);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename.endsWith(".csv") ? filename : `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
